import React, { useEffect, useContext, useState } from 'react';

import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { ModelInfoContext } from '../common/ModelInfoContext';
import {
  setConfig,
  trackModalClosed,
} from '../factories/analytics/analytics.service';
import { get } from 'lodash';
import { computePricing } from '../common/pricing/pricing';
import Body from '../steps/config/body/Body';
import Interior from '../steps/interior/Interior';
import Trims from '../steps/config/trims/Trims';
import Options from '../steps/options/Options';
import Wheels from '../steps/wheels/Wheels';
import Summary from '../steps/summary/Summary';
import Colors from '../steps/colors/Colors';
import Packages from '../steps/packages/Packages';
import MainTemplate from './mainTemplate/MainTemplate';
import AdChoices from './AdChoices';
import LegalNotificationModal from './legalNotificationModal/LegalNotificationModal';
import {
  hasBeenPreviouslyRead,
  updateExpiration,
} from './legalNotificationDisplayControl';
import MoreViewsTwoD from './moreViews/MoreViewsTwoD';
import OptionConflict from './optionConflict/OptionConflictContainer';
import computeSteps from '../utils/computeSteps';
import PostalCodeModal from './postalCodeModal/PostalCodeModal';
import { updatePostalCode } from '../store/actions';
import { CODE_TYPE_AKAMAI, CODE_TYPE_FALLBACK } from '../constants';
import SavedBuilds from '../steps/summary/savedBuilds/SavedBuilds';
import Analytics from '../common/analytics/Analytics';
import MoreViewsThreeD from './moreViews/MoreViewsThreeD';
import useQueryParams from '../hooks/useQueryParams';
import computeMoreViewsPath from '../utils/computeMoreViewPath';
import { translate } from '../utils/translate/translations';
import { getBaseUrl, getDeepUrl } from '../common/urlBuilder';
import { setOriginUrlAs20 } from '../resources/localStorage/localStorage.service';
import PackagesAndOptions from '../steps/packagesAndOptions/PackagesAndOptions';
import Models from '../steps/config/models/Models';
import Confirmation from '../views/confirmation/Confirmation';
import Error from '../views/error/Error';

const mapStateToProps = (state, ownProps) => {
  const {
    appResources,
    config,
    params,
    incentives,
    isUpdating,
    isDefaultPostalCode,
    api,
    urls,
    savedBuildCount,
    breakpoint,
    reservation,
  } = state;

  const {
    display: { cgiBackgroundColor, enableVisualizer },
  } = appResources;

  const pricing = computePricing(config.msrp, incentives);

  return {
    config,
    params,
    isUpdating,
    isDefaultPostalCode,
    pricing,
    uiPath: api.uiPath,
    urls,
    savedBuildCount,
    cgiBackgroundColor,
    enableVisualizer,
    breakpoint,
    reservation,
    ...ownProps,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onPostalCodeUpdate: (postalCode, data, isSummary, cb) => {
      dispatch(updatePostalCode(postalCode, data, isSummary, cb));
    },
  };
};

const Main = (props) => {
  const modelInfo = useContext(ModelInfoContext);
  const {
    pricing,
    config,
    params,
    isDefaultPostalCode,
    onPostalCodeUpdate,
    savedBuildCount,
    navRendered,
    shoppingLinks,
    cgiBackgroundColor,
    enableVisualizer,
    trimFeatures,
    basename,
    reservation,
  } = props;

  const disclaimer = translate('byo.page.general.disclaimer.msrp.simple');

  const location = useLocation();
  const search = location.search;
  const [skip3D, setSkip3D] = useState(false);
  const is2DParam = new URLSearchParams(search).get('force2D') === 'true';
  const { hasTransactionId } = modelInfo;

  useQueryParams(isDefaultPostalCode, params.postalCode);

  const render3D = enableVisualizer && !skip3D;

  const [isPostalCodeModalOpen, setIsPostalCodeModalOpen] = useState(
    isDefaultPostalCode
  );

  const [showLegalDisclosure, setShowLegalDisclosure] = useState(
    !hasBeenPreviouslyRead(new Date())
  );

  const [bottomOffset, setBottomOffset] = useState('0px');

  const steps = computeSteps(
    modelInfo.steps,
    config,
    location,
    basename,
    hasTransactionId,
    reservation
  );
  const { active: activeStep } = steps;
  const isSummaryPage = activeStep && activeStep.key !== 'summary';
  const defaultStep = get(steps, 'items[0].key');

  const [animateTrims, setAnimateTrims] = useState(true);
  const [animateBody, setAnimateBody] = useState(true);

  const moreViewsPath = computeMoreViewsPath(activeStep?.key);

  const animationCallback = (driveTypeToggle = false) => {
    if (driveTypeToggle) {
      setAnimateTrims(true);

      return;
    }

    if (animateTrims && activeStep.key === 'trims') {
      setAnimateTrims(false);
    }

    if (animateBody && activeStep.key === 'config') {
      setAnimateBody(false);
    }
  };

  useEffect(() => {
    setSkip3D(is2DParam);
  }, []);

  useEffect(() => {
    setConfig(params, config);
    setOriginUrlAs20(
      modelInfo,
      getDeepUrl(getBaseUrl(), config, params.postalCode)
    );
  }, [config]);

  const openPostalCodeModal = () => {
    setIsPostalCodeModalOpen(true);
  };

  const closePostalCodeModal = () => {
    const pathname = location.pathname;
    trackModalClosed(pathname, basename);
    setIsPostalCodeModalOpen(false);
  };

  const calculatePostalCodePlaceholder = () => {
    if (isDefaultPostalCode) {
      switch (params.codeType) {
        case CODE_TYPE_AKAMAI:
          return params.postalCode;
        case CODE_TYPE_FALLBACK:
        default:
          return params.postalCode;
      }
    }
    return params.postalCode;
  };

  return (
    <>
      {reservation.isPreOrderCompleted ? (
        <Routes>
          <Route
            path="/"
            element={
              <MainTemplate
                modelInfo={modelInfo}
                pricing={pricing}
                steps={steps}
                navRendered={navRendered}
                isNotificationOpen={
                  isPostalCodeModalOpen || showLegalDisclosure
                }
                setBottomOffset={setBottomOffset}
              />
            }>
            <Route
              path="summary/*"
              element={
                <Summary
                  pricing={pricing}
                  modelInfo={modelInfo}
                  onEditLocation={openPostalCodeModal}
                  moreViewsPath={moreViewsPath}
                />
              }
            />
            <Route path="confirmation/*" element={<Confirmation />} />
            <Route path="/" element={<Navigate to="summary" />} />
            <Route path="/*" element={<Navigate to="summary" />} />
          </Route>
        </Routes>
      ) : (
        <Routes>
          <Route path="error/*" element={<Error />} />
          <Route
            path="/"
            element={
              <MainTemplate
                modelInfo={modelInfo}
                pricing={pricing}
                steps={steps}
                navRendered={navRendered}
                isNotificationOpen={
                  isPostalCodeModalOpen || showLegalDisclosure
                }
                setBottomOffset={setBottomOffset}
              />
            }>
            <Route
              path="models/*"
              element={
                <Models
                  pricing={pricing}
                  disclaimer={disclaimer}
                  bottomOffset={bottomOffset}
                  animate={animateBody}
                  animationCallback={animationCallback}
                />
              }
            />
            <Route
              path="config/*"
              element={
                <Body
                  pricing={pricing}
                  disclaimer={disclaimer}
                  features={trimFeatures}
                  bottomOffset={bottomOffset}
                  animate={animateBody}
                  animationCallback={animationCallback}
                />
              }
            />
            <Route
              path="trims/*"
              element={
                <Trims
                  features={trimFeatures}
                  disclaimer={disclaimer}
                  pricing={pricing}
                  modelInfo={modelInfo}
                  bottomOffset={bottomOffset}
                  setBottomOffset={setBottomOffset}
                  animate={animateTrims}
                  animationCallback={animationCallback}
                  reservation={reservation}
                />
              }
            />
            <Route
              path="exterior/*"
              element={
                <Colors
                  bottomOffset={bottomOffset}
                  moreViewsPath={moreViewsPath}
                />
              }
            />
            <Route
              path="wheels/*"
              element={
                <Wheels
                  bottomOffset={bottomOffset}
                  moreViewsPath={moreViewsPath}
                />
              }
            />
            <Route
              path="interior/*"
              element={
                <Interior
                  bottomOffset={bottomOffset}
                  moreViewsPath={moreViewsPath}
                />
              }
            />
            <Route
              path="packagesandoptions/*"
              element={<PackagesAndOptions bottomOffset={bottomOffset} />}
            />
            <Route
              path="packages/*"
              element={<Packages bottomOffset={bottomOffset} />}
            />
            <Route
              path="options/*"
              element={<Options bottomOffset={bottomOffset} />}
            />
            <Route
              path="summary/*"
              element={
                <Summary
                  pricing={pricing}
                  modelInfo={modelInfo}
                  onEditLocation={openPostalCodeModal}
                  moreViewsPath={moreViewsPath}
                />
              }
            />
            <Route path="confirmation/*" element={<Confirmation />} />
            <Route path="/" element={<Navigate to={defaultStep} />} />
          </Route>
        </Routes>
      )}
      <Routes>
        <Route path={`:parent/ads`} element={<AdChoices />} />
        {/* TODO: Remove the corvette case once the deployment to production happens and enableVisualizer has correct vals */}
        <Route
          path={render3D ? `:parent/*` : `:parent/moreviews`}
          element={
            render3D ? (
              <MoreViewsThreeD cgiBackgroundColor={cgiBackgroundColor} />
            ) : (
              <MoreViewsTwoD cgiBackgroundColor={cgiBackgroundColor} />
            )
          }
        />
        <Route
          path={`:parent/savedbuilds`}
          element={
            <SavedBuilds
              postalCode={params.postalCode}
              count={savedBuildCount}
              config={config}
              shoppingLinks={shoppingLinks}
              language={params.lang}
            />
          }
        />
      </Routes>
      <OptionConflict modelInfo={modelInfo} basename={basename} />
      {isPostalCodeModalOpen && (
        <PostalCodeModal
          fallbackPostalCode={params.postalCode}
          initialValue={calculatePostalCodePlaceholder()}
          onClose={closePostalCodeModal}
          isDefaultPostalCode={isDefaultPostalCode}
          onUpdate={(postalCode, data, cb) => {
            onPostalCodeUpdate(postalCode, data, !!isSummaryPage, cb);
          }}
          basename={basename}
        />
      )}
      <Analytics basename={basename} />
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Main);
